import React, { useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import Button from '../../components/common/Button';
import Drawer from '../../components/common/Drawer';
import SimpleAlert from '../../components/common/SimpleAlert';
import CompetitionEntryDataCard from '../dataCards/CompetitionEntryDataCard';
import Payment, { PaymentCallbacks, PaymentConfig } from '../drawer/Payment';
import CompetitionScoreForm from './CompetitionScoreForm';
import DownloadCompetitionTargets from '../drawer/DownloadCompetitionTargets';
import {
  CompetitionClassDTO,
  CompetitionDTO,
  CompetitionEntryDTO,
  CompetitionStatusDTO,
  MemberDTO,
  ScoreDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import { useMemberApi } from '../../hooks/useMemberApi';
import { useCompetitionApi } from '../../hooks/useCompetitionApi';
import { useCompetitionClassApi } from '../../hooks/useCompetitionClassApi';
import { useCompetitionStatusApi } from '../../hooks/useCompetitionStatusApi';
import { useCompetitionEntryApi } from '../../hooks/useCompetitionEntryApi';
import { useScoreApi } from '../../hooks/useScoreApi';
import { getMemberId } from '../../utils/tokenUtils';
import { ComponentConstants } from '../../constants/ComponentConstants';
import { ScoreTypeConstants } from '../../constants/ScoreTypeConstants';
import { confirmAction, showAlert } from '../../utils/sweetAlertUtils';
import { Loader2 } from 'lucide-react';
import { useProvinces } from '../../hooks/useProvinceApi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePayFastApi } from '../../hooks/usePayFastApi';
import ButtonLegendCta from '../common/ButtonLegendCta';
import { getButtonLegendItems } from '../../utils/buttonLegendDisplayUtils';

type View = 'loading' | 'form' | 'entries' | 'nocompetition';

const legendItems = getButtonLegendItems('competitionEntry');

interface CompetitionEntryFormProps {
  initialValues: CompetitionEntryDTO;
  onSubmit: (data: CompetitionEntryDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  competitionEntry: {
    id: 'competition-entry',
    title: 'Competition Entry',
  },
  myCompetitions: {
    id: 'my-competitions',
    title: 'My Competitions',
  },
} as const;

const CompetitionEntryForm: React.FC<CompetitionEntryFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const memberId = getMemberId();
  const { getById: getMember } = useMemberApi();
  const { search: searchCompetitions } = useCompetitionApi();
  const { search: searchClasses } = useCompetitionClassApi();
  const { search: searchStatuses } = useCompetitionStatusApi();
  const { search: searchEntries, updateProofOfPayment } = useCompetitionEntryApi();
  const { create: createScore, search: searchScores, isLoading: isScoreSubmitting } = useScoreApi();
  const { data: provinces, isLoading: isLoadingProvinces } = useProvinces();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Captured once, when a scanned target QR code deep-links into this page — kept as state
  // (rather than read live from the URL) so it can't leak into unrelated, organically-opened
  // score submissions for other entries later in the same page session.
  const [qrScoreContext, setQrScoreContext] = useState<{ exerciseTypeId?: string; exerciseSpecificId?: string } | null>(null);

  const [formData, setFormData] = useState<CompetitionEntryDTO>(initialValues);
  const [view, setView] = useState<View>('loading');
  const [member, setMember] = useState<MemberDTO | null>(null);
  const [competitions, setCompetitions] = useState<CompetitionDTO[]>([]);
  const [activeCompetition, setActiveCompetition] = useState<CompetitionDTO | null>(null);
  const [entries, setEntries] = useState<CompetitionEntryDTO[]>([]);
  const [submittedEntryIds, setSubmittedEntryIds] = useState<Set<string>>(new Set());
  const [competitionClasses, setCompetitionClasses] = useState<CompetitionClassDTO[]>([]);
  const [competitionStatuses, setCompetitionStatuses] = useState<CompetitionStatusDTO[]>([]);
  const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = useState(false);
  const [isScoreDrawerOpen, setIsScoreDrawerOpen] = useState(false);
  const [isTargetsDrawerOpen, setIsTargetsDrawerOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<CompetitionEntryDTO | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [competitionPayFastFee, setCompetitionPayFastFee] = useState<number | undefined>(undefined);
  const [competitionTotalWithPayFastFee, setCompetitionTotalWithPayFastFee] = useState<number | undefined>(undefined);

  const year = new Date().getFullYear();

  const validationRules: ValidationRules = {
    courierStreet: [{ required: true, message: 'Courier Street is required' }],
    courierTown: [{ required: true, message: 'Courier Town is required' }],
    courierSuburb: [{ required: true, message: 'Courier Suburb is required' }],
    courierProvinceId: [{ required: true, message: 'Courier Province is required' }],
    courierCode: [{ required: true, message: 'Courier Code is required' }],
  };

  const { errors, validate } = useFormValidation<CompetitionEntryDTO>(validationRules);

  const loadSubmittedScores = async () => {
    if (!memberId) return;
    try {
      const res = await searchScores({
        filters: { memberId, scoreTypeId: ScoreTypeConstants.COMPETITION },
        page: 1,
        pageSize: 9999,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      const scoredEntryIds = (res?.data?.results ?? [])
        .map((s) => s.competitionEntryId)
        .filter((id): id is string => !!id);
      setSubmittedEntryIds(new Set(scoredEntryIds));
    } catch (err) {
      console.error('Error loading submitted scores:', err);
    }
  };

  const loadEntries = async () => {
    if (!memberId) return;
    try {
      const res = await searchEntries({
        filters: { memberId },
        page: currentPage,
        pageSize,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setEntries(res?.data?.results ?? []);
      setTotalPages(Math.ceil((res?.data?.totalCount ?? 0) / pageSize) || 1);
      await loadSubmittedScores();
    } catch (err) {
      console.error('Error loading entries:', err);
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!memberId) {
        setView('nocompetition');
        return;
      }
      try {
        const [memberRes, compRes, classRes, statusRes, entryRes] = await Promise.all([
          getMember(memberId),
          searchCompetitions({ filters: {}, page: 1, pageSize: 9999, sortBy: 'StartDate', sortDescending: false }),
          searchClasses({ page: 1, pageSize: 9999, sortBy: 'DisplayPosition', sortDescending: false }),
          searchStatuses({ page: 1, pageSize: 9999, sortBy: 'DisplayPosition', sortDescending: false }),
          searchEntries({ filters: { memberId }, page: 1, pageSize: 9999, sortBy: 'CreatedUtc', sortDescending: true }),
        ]);

        const m: MemberDTO = memberRes?.data ?? {};
        setMember(m);

        const allCompetitions: CompetitionDTO[] = compRes?.data?.results ?? [];
        setCompetitions(allCompetitions);
        setCompetitionClasses(classRes?.data?.results ?? []);
        setCompetitionStatuses(statusRes?.data?.results ?? []);

        const memberEntries: CompetitionEntryDTO[] = entryRes?.data?.results ?? [];
        setEntries(memberEntries);
        setTotalPages(Math.ceil((entryRes?.data?.totalCount ?? memberEntries.length) / pageSize) || 1);
        await loadSubmittedScores();

        const now = new Date();
        const active = allCompetitions.find((c) => {
          const showFrom = new Date(c.showBannerDate ?? '');
          const regOpen = new Date(c.registrationOpenDate ?? '');
          const regClose = new Date(c.registrationCloseDate ?? '');
          return showFrom <= now && regOpen <= now && now <= regClose;
        }) ?? null;
        setActiveCompetition(active);

        const age = m.dateOfBirth
          ? Math.floor((Date.now() - new Date(m.dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
          : 0;

        setFormData({
          ...initialValues,
          memberId,
          competitionId: active?.id,
          year,
          age,
          courierAddress: m.physicalAddress,
          courierStreet: m.physicalAddress?.split(',')[0] || '',
          courierSuburb: m.physicalSuburb,
          courierTown: m.physicalTown,
          courierProvinceId: m.provinceId,
          courierCode: m.physicalPostalCode,
        });

        const activeEntries = active
          ? memberEntries.filter((e) => e.competitionId?.toLowerCase() === active.id?.toLowerCase())
          : [];

        if (activeEntries.length > 0) {
          setView('entries');
        } else if (active) {
          setView('form');
        } else if (memberEntries.length > 0) {
          setView('entries');
        } else {
          setView('nocompetition');
        }
      } catch (err) {
        console.error('Error loading competition entry data:', err);
        setView('nocompetition');
      }
    };

    load();
  }, [memberId]);

  // Deep link from a scanned target QR code: auto-open the score drawer for the entry in the URL,
  // capture its exercise context into state, then strip the query params so they can't leak into
  // later, organically-opened score submissions during the same page session.
  useEffect(() => {
    if (view !== 'entries' || searchParams.get('openScore') !== '1' || !initialValues.id) return;

    const entry = entries.find((e) => e.id?.toLowerCase() === initialValues.id?.toLowerCase());
    if (!entry) return;

    setQrScoreContext({
      exerciseTypeId: searchParams.get('exerciseTypeId') || undefined,
      exerciseSpecificId: searchParams.get('exerciseSpecificId') || undefined,
    });
    setSelectedEntry(entry);
    setIsScoreDrawerOpen(true);
    setSearchParams({}, { replace: true });
  }, [view, entries, searchParams, initialValues.id, setSearchParams]);

  const handleSave = async () => {
    if (!validate(formData)) return;

    const confirmed = await confirmAction({
      title: 'Confirm Competition Entry',
      text: 'I concur that I am entering the competition and will follow the competition rules as set out by Natshoot.',
      icon: 'warning',
      confirmButtonText: 'Yes, I confirm',
      cancelButtonText: 'Cancel',
    });

    if (!confirmed) return;

    onSubmit(formData);
  };

  const handleMakePayment = (entry: CompetitionEntryDTO) => {
    setSelectedEntry(entry);
    setIsPaymentDrawerOpen(true);
  };

  const handlePaymentClose = () => {
    setIsPaymentDrawerOpen(false);
    setSelectedEntry(null);
  };

  const handlePaymentComplete = async () => {
    setIsPaymentDrawerOpen(false);
    setSelectedEntry(null);
    await loadEntries();
  };

  const handleSubmitScore = (entry: CompetitionEntryDTO) => {
    setQrScoreContext(null);
    setSelectedEntry(entry);
    setIsScoreDrawerOpen(true);
  };

  const handleScoreDrawerClose = () => {
    setIsScoreDrawerOpen(false);
    setSelectedEntry(null);
    setQrScoreContext(null);
  };

  const handleDownloadTargets = (entry: CompetitionEntryDTO) => {
    setSelectedEntry(entry);
    setIsTargetsDrawerOpen(true);
  };

  const handleTargetsDrawerClose = () => {
    setIsTargetsDrawerOpen(false);
    setSelectedEntry(null);
  };

  const handleScoreSubmit = async (data: ScoreDTO) => {
    try {
      // qrScoreContext is only ever populated by the QR deep-link effect above, and is reset to
      // null whenever the score drawer is opened manually (handleSubmitScore) — so its presence
      // is the authoritative signal for whether this submission came from a scanned target.
      await createScore({ ...data, memberId: memberId ?? undefined, isQrSubmission: qrScoreContext !== null });
      navigate('/admin/scores')
    } catch (error: any) {
      console.error('Error submitting competition score:', error);
      await showAlert({
        title: 'Unable to Submit Score',
        text: error?.message || 'An error occurred while submitting your competition score. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const handleProofUploaded = async (fileRecordId: string) => {
    if (selectedEntry?.id) {
      await updateProofOfPayment(selectedEntry.id, fileRecordId);
    }
    await handlePaymentComplete();
  };

  const selectedCompetition = competitions.find(
    (c) => c.id?.toLowerCase() === selectedEntry?.competitionId?.toLowerCase(),
  );

  const { calculateFee } = usePayFastApi();

  useEffect(() => {
    const amount = selectedCompetition?.amount;
    if (!amount || amount <= 0) {
      setCompetitionPayFastFee(undefined);
      setCompetitionTotalWithPayFastFee(undefined);
      return;
    }

    let cancelled = false;
    calculateFee(amount)
      .then((response) => {
        if (cancelled || !response?.data) return;
        setCompetitionPayFastFee(response.data.payFastFee);
        setCompetitionTotalWithPayFastFee(response.data.totalWithFee);
      })
      .catch((error) => {
        console.error('Error calculating Competition Entry PayFast fee:', error);
        if (!cancelled) {
          setCompetitionPayFastFee(undefined);
          setCompetitionTotalWithPayFastFee(undefined);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompetition?.amount]);

  const paymentConfig: PaymentConfig = {
    amount: selectedCompetition?.amount ?? 0,
    itemName: `Competition Entry - ${selectedCompetition?.name ?? ''}`,
    description: 'NHSA National Postal Competition Entry',
    reference: member?.idPassportNumber ?? '',
    allowOnlinePayment: true,
    allowEftPayment: true,
    allowDebitOrder: false,
    payFastTransactionFee: competitionPayFastFee,
    totalWithPayFastFee: competitionTotalWithPayFastFee,
  };

  const paymentCallbacks: PaymentCallbacks = {
    onPaymentSuccess: handlePaymentComplete,
    onProofUploaded: handleProofUploaded,
    onProofPending: handlePaymentClose,
    onCancel: handlePaymentClose,
  };

  const sections = Object.values(FORM_SECTIONS);

  if (view === 'loading') {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="animate-spin h-8 w-8 text-gray-400" />
      </div>
    );
  }

  if (view === 'nocompetition') {
    return (
      <div className="p-5">
        <SimpleAlert variant="warning" message="No competition with open registration was found." />
      </div>
    );
  }

  if (view === 'entries') {
    return (
      <>
        <FormSection
          id={FORM_SECTIONS.myCompetitions.id}
          title={FORM_SECTIONS.myCompetitions.title}
          columns={1}
          headerActions={
            <div className="flex items-center gap-2">
              <ButtonLegendCta items={legendItems} />
              <Button variant="secondary" onClick={() => navigate('/admin/competitionresults/my-results')}>
                Check Live Results
              </Button>
            </div>
          }
        >
          <div className="p-5">
            <CompetitionEntryDataCard
              entries={entries}
              competitions={competitions}
              competitionClasses={competitionClasses}
              competitionStatuses={competitionStatuses}
              submittedEntryIds={submittedEntryIds}
              onMakePayment={handleMakePayment}
              onSubmitScore={handleSubmitScore}
              onDownloadTargets={handleDownloadTargets}
              isLoading={false}
              currentPage={currentPage}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          </div>
        </FormSection>
        <Drawer isOpen={isPaymentDrawerOpen} onClose={handlePaymentClose} title="Make a Payment">
          {member && selectedEntry && (
            <Payment
              member={member}
              competitionEntry={selectedEntry}
              componentId={ComponentConstants.COMPETITION}
              paymentConfig={paymentConfig}
              callbacks={paymentCallbacks}
              title="Competition Entry Payment"
              showMemberDetails={true}
              type="competition"
            />
          )}
        </Drawer>
        <Drawer isOpen={isScoreDrawerOpen} onClose={handleScoreDrawerClose} title="Submit Competition Score" size="large">
          {selectedEntry && (
            <CompetitionScoreForm
              initialValues={{
                competitionEntryId: selectedEntry.id,
                memberId: memberId ?? undefined,
                exerciseTypeId: qrScoreContext?.exerciseTypeId,
                exerciseSpecificId: qrScoreContext?.exerciseSpecificId,
              } as ScoreDTO}
              onSubmit={handleScoreSubmit}
              onCancel={handleScoreDrawerClose}
              isLoading={isScoreSubmitting}
            />
          )}
        </Drawer>
        <Drawer isOpen={isTargetsDrawerOpen} onClose={handleTargetsDrawerClose} title="Download Competition Targets">
          {memberId && selectedEntry?.id && (
            <DownloadCompetitionTargets
              memberId={memberId}
              competitionEntryId={selectedEntry.id}
              onClose={handleTargetsDrawerClose}
            />
          )}
        </Drawer>
      </>
    );
  }

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel} isSubmitting={isLoading}>
      <SimpleAlert
        variant='info'
        message={
          <>
            Please find the Annual National Competition information{' '}
            <a
              href={activeCompetition?.rulesTargetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-medium"
            >
              here
            </a>
          </>
        }
      />
      <FormSection
        id={FORM_SECTIONS.competitionEntry.id}
        title={FORM_SECTIONS.competitionEntry.title}
      >
        <SimpleAlert
          variant='warning'
          message={`Registration closes on ${activeCompetition?.registrationCloseDate
            ? new Date(activeCompetition.registrationCloseDate).toLocaleDateString()
            : ''
            }, please ensure you complete your registration and payment before then.`}
        />
        <SimpleAlert variant='info' message="Please complete the courier address below to have your medals/trophies (if applicable) delivered. We can not be held responsible if the address is incorrect.
Our office will be in contact with you if your courier address is too far away from main and/or satellite hubs to be easily serviced by our courier."/>
        <FormField
          id="courierStreet"
          label="Courier Street"
          type="text"
          value={formData.courierStreet || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Courier Street"
          required={true}
          error={errors.courierStreet}
        />
        <FormField
          id="courierTown"
          label="Courier Town"
          type="text"
          value={formData.courierTown || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Courier Town"
          required={true}
          error={errors.courierTown}
        />
        <FormField
          id="courierSuburb"
          label="Courier Suburb"
          type="text"
          value={formData.courierSuburb || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Courier Suburb"
          required={true}
          error={errors.courierSuburb}
        />
        <FormField
          id="courierProvinceId"
          label="Courier Province"
          type="dropdown"
          value={formData.courierProvinceId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Courier Province"
          required={true}
          error={errors.courierProvinceId}
          options={(provinces || []).map((type) => ({
            value: type.id ?? '',
            label: type.title ?? '',
          }))}
          isLoading={isLoadingProvinces}
        />
        <FormField
          id="courierCode"
          label="Courier Code"
          type="text"
          value={formData.courierCode || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Courier Code"
          required={true}
          error={errors.courierCode}
        />
        <FormField
          id="courierNote"
          label="Courier Note"
          type="textarea"
          value={formData.courierNote || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Courier Note"
          required={false}
          error={errors.courierNote}
        />
      </FormSection>
    </Form>
  );
};

export default CompetitionEntryForm;